
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

// N-299 — TinyMCE 8 lo sirve el propio panel (lo copia `vue.config.js` a `dist/tinymce/`), sin
// la nube de Tiny: Tiny cobraba cada editor que arrancaba. Con la licencia GPL (`license_key: 'gpl'`)
// y sin API key no llama a ningún sitio.
const SCRIPT_TINYMCE = '/tinymce/tinymce.min.js';

const BARRA =
	'undo redo | forecolor backcolor | bold italic underline | aligncenter alignjustify alignleft alignright alignnone | bullist numlist | link | removeformat ';

// N-300 — Descripción y No incluye: 250 px vacías, 600 como mucho (2/3 de una pantalla de 900).
const ALTO_MINIMO_AL_CRECER = 250;
const ALTO_MAXIMO_AL_CRECER = 600;

// Un solo <script> para todos los editores de la página: el primero que lo pide lo carga y
// los demás esperan a la misma promesa. Si falla, se olvida para que el siguiente lo reintente.
let cargaDeTinymce: Promise<any> | null = null;

function cargarTinymce(): Promise<any> {
	const w = window as any;
	if (w.tinymce) {
		return Promise.resolve(w.tinymce);
	}
	if (!cargaDeTinymce) {
		cargaDeTinymce = new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.src = SCRIPT_TINYMCE;
			script.addEventListener('load', () => resolve(w.tinymce));
			script.addEventListener('error', () => {
				cargaDeTinymce = null;
				reject(new Error('No se pudo cargar ' + SCRIPT_TINYMCE));
			});
			document.head.appendChild(script);
		});
	}
	return cargaDeTinymce;
}

@Component
export default class TextEditor extends Vue {
	@Prop({ default: null, required: false }) private model;
	@Prop({ default: function () {return []}, required: false }) private errors;
	@Prop({ default: null, required: false }) private height;
	@Prop({ default: null, required: false }) private id;
	@Prop({ default: false, required: false }) private hasInfoButton;
	@Prop({ default: null, required: false }) private label; // M-40 — aditiva: sin ella, nada cambia
	// N-300 — aditiva: solo Descripción y No incluye de las fichas la llevan; sin ella, 200 px como hasta ahora.
	// Boolean: así un `crece` sin valor en la plantilla llega como true (sin tipo llegaría '' y no crecería).
	@Prop({ type: Boolean, default: false, required: false }) private crece;

	@Watch('model')
	modelChange(value) {
		this.value = value;
		// Lo que viene de fuera entra en el editor; lo que acaba de escribir el usuario ya está.
		if (this.editor && this.editor.getContent() !== (value || '')) {
			this.editor.setContent(value || '');
		}
	}

	private value = '';
	private editor: any = null;
	private destruido = false;

	private config: { [clave: string]: any } = {
		language: 'es',
		language_url: '/tinymce/langs/es.js',
		license_key: 'gpl',
		promotion: false,
		// La 5 los dibujaba de 200 px y la 8 trae 400: con diez editores la ficha doblaba su largo.
		height: 200,
		menubar: false,
		paste_as_text: true,
		// `paste` ya no es un plugin desde la 6: es del núcleo, y `paste_as_text` sigue valiendo.
		plugins: 'advlist lists autolink link charmap preview anchor insertdatetime media wordcount',
		toolbar: BARRA,
		content_style: `
    .alert {
		width: 100%;
		box-sizing: border-box;
		padding: 12px;
		display: block;
		margin: 1rem 0;
		border-radius: 8px;
		font-size: 14px;
		line-height: 1.5em;
		border: 0;
    }
    .alert-info {
      color: #057c7c;
      background-color: #def8f3;
    }
    .alert-warning {
      color: #dd2526;
      background-color: #fef2f2;
    }
  `,
		setup: function (editor) {
			// Botón para alert-info
			editor.ui.registry.addButton('infoButton', {
				text: 'Info', // Texto del botón
				onAction: function () {
					editor.insertContent('<div class="alert alert-info" contenteditable="true">Escribe aquí tu información...</div><br>');
				}
			});

			// Botón para alert-danger
			editor.ui.registry.addButton('alertButton', {
				text: 'Alerta', // Texto del botón
				onAction: function () {
					editor.insertContent('<div class="alert alert-warning" contenteditable="true">Escribe aquí tu alerta...</div><br>');
				}
			});
		}


	};

	mounted() {
		if (this.model) {
			this.value = this.model;
		}
		// N-299: la barra se decide ANTES de arrancar. Antes se añadía después y se cambiaba la
		// `key` del editor para que la cogiera: cada editor con Info/Alerta nacía dos veces.
		if (this.hasInfoButton)
		{
			this.config.toolbar += ' | infoButton  alertButton';
		}
		// if (this.height) {
		// 	this.config.height = this.height;
		// }
		// N-300: la caja crece con el texto hasta un tope y luego hace scroll dentro. Sin asa en la
		// esquina: con autoresize, lo que se estira a mano se deshace en la siguiente tecla.
		// (No se reaviva `height`: los editores de precios pasan `:height="100"` y bajarían a 100.)
		if (this.crece) {
			delete this.config.height;
			this.config.plugins += ' autoresize';
			this.config.min_height = ALTO_MINIMO_AL_CRECER;
			this.config.max_height = ALTO_MAXIMO_AL_CRECER;
			this.config.resize = false;
		}
		cargarTinymce().then((tinymce) => this.arrancar(tinymce));
	}

	beforeDestroy() {
		this.destruido = true;
		if (this.editor) {
			(window as any).tinymce.remove(this.editor);
			this.editor = null;
		}
	}

	private arrancar(tinymce) {
		if (this.destruido) {
			return;
		}
		const setupDeLosBotones = this.config.setup;
		tinymce.init({
			...this.config,
			target: this.$refs.sitio,
			setup: (editor) => {
				this.editor = editor;
				setupDeLosBotones(editor);
				editor.on('init', () => {
					editor.setContent(this.value || '');
				});
				// Los mismos eventos con los que el wrapper oficial de Vue (el de antes) movía el v-model.
				editor.on('change input undo redo', () => {
					this.value = editor.getContent();
					this.$emit('input', this.value);
				});
			},
		});
	}
}
